import React, { useState, useEffect, useCallback, memo } from 'react';
import './App.css';
import getCurvePoints from './utils/curve_calc.js';

// Memoized Form Component
const FormPage = memo(({ 
  formStep, 
  formData, 
  isSubmitting,
  onFormClose, 
  onNameSubmit, 
  onNameChange,
  onEmailSubmit,
  onEmailChange,
  onLocationSubmit,
  onLocationChange,
  onBirthYearSubmit,
  onBirthYearChange,
  onTrackSubmit,
  onTrackChange,
  onTeamTypeSubmit,
  onTeamTypeChange,
  onTeamMembersSubmit,
  onTeamMembersChange,
  onProjectNameSubmit,
  onProjectNameChange,
  onShortDescriptionSubmit,
  onShortDescriptionChange,
  onDetailedDescriptionSubmit,
  onDetailedDescriptionChange,
  onHowBuiltSubmit,
  onHowBuiltChange,
  onGithubLinkSubmit,
  onGithubLinkChange,
  onVideoLinkSubmit,
  onVideoLinkChange
}) => (
  <div className="form-page">
    <div className="form-overlay" onClick={onFormClose}></div>
    <div className="form-container">
      <div className="form-header">
        <div className="form-close" onClick={onFormClose}>×</div>
        <div className="form-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(formStep / (formData.teamType === 'Team' ? 14 : 13)) * 100}%` }}></div>
          </div>
          <div className="progress-text">Step {formStep} of {formData.teamType === 'Team' ? 14 : 13}</div>
        </div>
      </div>
      
      <div className="form-content">
        {/* Step 1: Name */}
        {formStep === 1 && (
          <div className="form-step">
            <h2 className="form-title">What can we call you?</h2>
            <p className="form-description">Let's start with your name to personalize your experience.</p>
            <form onSubmit={onNameSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="text"
                  value={formData.name}
                  onChange={onNameChange}
                  placeholder="Enter your name"
                  className="form-input"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}
        
        {/* Step 2: Email */}
        {formStep === 2 && (
          <div className="form-step">
            <h2 className="form-title">What is your email address?</h2>
            <p className="form-description">We'll use this to keep you updated.</p>
            <form onSubmit={onEmailSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="email"
                  value={formData.email}
                  onChange={onEmailChange}
                  placeholder="Enter your email"
                  className="form-input"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Location */}
        {formStep === 3 && (
          <div className="form-step">
            <h2 className="form-title">Where are you from?</h2>
            <p className="form-description">Tell us about your location.</p>
            <form onSubmit={onLocationSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="text"
                  value={formData.location}
                  onChange={onLocationChange}
                  placeholder="Enter your location"
                  className="form-input"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 4: Birth Year */}
        {formStep === 4 && (
          <div className="form-step">
            <h2 className="form-title">What is your birth year?</h2>
            <p className="form-description">This helps us understand our community better.</p>
            <form onSubmit={onBirthYearSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="number"
                  value={formData.birthYear}
                  onChange={onBirthYearChange}
                  placeholder="Enter your birth year"
                  className="form-input"
                  min="1900"
                  max="2018"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 5: Track Selection */}
        {formStep === 5 && (
          <div className="form-step">
            <h2 className="form-title">What tracks you want to apply?</h2>
            <p className="form-description">Choose the track that interests you most.</p>
            <form onSubmit={onTrackSubmit} className="form-form">
              <div className="form-options">
                {['AI & ML', 'Robotics, hardware & IoT', 'Blockchain & Cryptography', 'Other'].map((track) => (
                  <label key={track} className="form-option">
                    <input
                      type="radio"
                      name="track"
                      value={track}
                      checked={formData.track === track}
                      onChange={() => onTrackChange(track)}
                      className="form-radio"
                    />
                    <span className="form-option-text">{track}</span>
                  </label>
                ))}
              </div>
              <button type="submit" className="form-button primary" disabled={!formData.track}>
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 6: Team Type */}
        {formStep === 6 && (
          <div className="form-step">
            <h2 className="form-title">Did you work as a solo or in team?</h2>
            <p className="form-description">Let us know your preferred working style.</p>
            <form onSubmit={onTeamTypeSubmit} className="form-form">
              <div className="form-options">
                {['Solo', 'Team'].map((teamType) => (
                  <label key={teamType} className="form-option">
                    <input
                      type="radio"
                      name="teamType"
                      value={teamType}
                      checked={formData.teamType === teamType}
                      onChange={() => onTeamTypeChange(teamType)}
                      className="form-radio"
                    />
                    <span className="form-option-text">{teamType}</span>
                  </label>
                ))}
              </div>
              <button type="submit" className="form-button primary" disabled={!formData.teamType}>
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 7: Team Members (only for teams) */}
        {formStep === 7 && formData.teamType === 'Team' && (
          <div className="form-step">
            <h2 className="form-title">Team Members</h2>
            <p className="form-description">Please list the names of your team members.</p>
            <p className="form-note">Note: Each member needs to submit their own form</p>
            <form onSubmit={onTeamMembersSubmit} className="form-form">
              <div className="form-group">
                <textarea
                  value={formData.teamMembers}
                  onChange={onTeamMembersChange}
                  placeholder="Enter team member names (one per line)"
                  className="form-textarea"
                  required
                  autoFocus
                  rows={4}
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 8: Project Name */}
        {formStep === 8 && (
          <div className="form-step">
            <h2 className="form-title">Project Name</h2>
            <p className="form-description">What's the name of your project?</p>
            <form onSubmit={onProjectNameSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="text"
                  value={formData.projectName}
                  onChange={onProjectNameChange}
                  placeholder="Enter your project name"
                  className="form-input"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 9: Short Description */}
        {formStep === 9 && (
          <div className="form-step">
            <h2 className="form-title">Short Description</h2>
            <p className="form-description">Give us a brief overview of your project (1–2 sentences).</p>
            <form onSubmit={onShortDescriptionSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="text"
                  value={formData.shortDescription}
                  onChange={onShortDescriptionChange}
                  placeholder="Brief description of your project..."
                  className="form-input"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 10: Detailed Description */}
        {formStep === 10 && (
          <div className="form-step">
            <h2 className="form-title">Detailed Description</h2>
            <p className="form-description">Tell us what it does, how it works, and why it matters.</p>
            <form onSubmit={onDetailedDescriptionSubmit} className="form-form">
              <div className="form-group">
                <textarea
                  value={formData.detailedDescription}
                  onChange={onDetailedDescriptionChange}
                  placeholder="Detailed description: what it does, how it works, why it matters..."
                  className="form-textarea"
                  required
                  autoFocus
                  rows={4}
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 11: How Built */}
        {formStep === 11 && (
          <div className="form-step">
            <h2 className="form-title">How did you build it?</h2>
            <p className="form-description">Tell us about the technologies, tools, and approach you used.</p>
            <form onSubmit={onHowBuiltSubmit} className="form-form">
              <div className="form-group">
                <textarea
                  value={formData.howBuilt}
                  onChange={onHowBuiltChange}
                  placeholder="Describe how you built your project..."
                  className="form-textarea"
                  required
                  autoFocus
                  rows={4}
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 12: GitHub Link */}
        {formStep === 12 && (
          <div className="form-step">
            <h2 className="form-title">GitHub Link</h2>
            <p className="form-description">Share the link to your project's GitHub repository.</p>
            <form onSubmit={onGithubLinkSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="url"
                  value={formData.githubLink}
                  onChange={onGithubLinkChange}
                  placeholder="https://github.com/username/repository"
                  className="form-input"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 13: Video Link */}
        {formStep === 13 && (
          <div className="form-step">
            <h2 className="form-title">Video Demo</h2>
            <p className="form-description">Share a video link showcasing your project (under 2 minutes).</p>
            <form onSubmit={onVideoLinkSubmit} className="form-form">
              <div className="form-group">
                <input
                  type="url"
                  value={formData.videoLink}
                  onChange={onVideoLinkChange}
                  placeholder="https://youtube.com/watch?v=... or other video platform"
                  className="form-input"
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="form-button primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Complete'}
              </button>
            </form>
          </div>
        )}

        {/* Step 14: Success */}
        {formStep === 14 && (
          <div className="form-step">
            <h2 className="form-title">Thank you, {formData.name}!</h2>
            <p className="form-description">Your application has been submitted successfully. We'll be in touch soon!</p>
            <div className="form-actions">
              <button onClick={onFormClose} className="form-button primary">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
));

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loginText, setLoginText] = useState('[Submit]');
  const [currentPage, setCurrentPage] = useState('home');
  const [showForm, setShowForm] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    birthYear: '',
    track: '',
    teamType: '',
    teamMembers: '',
    projectName: '',
    shortDescription: '',
    detailedDescription: '',
    howBuilt: '',
    githubLink: '',
    videoLink: ''
  });


  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const nowScrolled = scrollTop > 100;
      setIsScrolled(nowScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cursor Trail Effect - must be before any early returns
  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.id = 'cursor-trail';
    canvas.style.cssText = 'position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; pointer-events: none; z-index: 9999; background: transparent;';
    document.body.appendChild(canvas);
    
    const context = canvas.getContext('2d');
    const circleRadius = 20;
    const maxIterations = 200;
    const rateOfIterationDecay = 0.08;
    // Use a light blue color (similar to randomColor with luminosity: 'light')
    const trailColor = '#ADD8E6'; // Light blue (pastel)
    
    let circles = [];
    let prevCursor = null;
    let animationFrame = null;
    let isActive = false;
    let viewportWidth = window.innerWidth;
    let viewportHeight = window.innerHeight;
    
    const updateCanvasSize = () => {
      viewportWidth = window.innerWidth;
      viewportHeight = window.innerHeight;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = viewportWidth * dpr;
      canvas.height = viewportHeight * dpr;
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.scale(dpr, dpr);
    };
    
    const cursorPositionInViewport = (event) => {
      if (event.touches && event.touches[0]) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      return { x: event.clientX, y: event.clientY };
    };
    
    // Use the same curve calculation as the original
    const pointsBetweenTwoPoints = (point1, point2) => {
      // [x1, y1, x2, y2, ...]
      const splinePoints = getCurvePoints([point1.x, point1.y, point2.x, point2.y]);
      // [{x1, y1}, {x2, y2}, ...]
      const points = [];
      let prevX;
      splinePoints.forEach((value, index) => {
        value = Math.round(value);
        const isX = index % 2 === 0;
        if (isX) {
          prevX = value;
        } else {
          const point = { x: prevX, y: value };
          points.push(point);
        }
      });
      return points;
    };
    
    const startTrail = (event) => {
      isActive = true;
      prevCursor = null;
      const position = cursorPositionInViewport(event);
      createCircle(position);
    };
    
    const updateTrail = (event) => {
      if (!isActive) return;
      const position = cursorPositionInViewport(event);
      createCircle(position);
      startAnimation();
    };
    
    const stopTrail = () => {
      isActive = false;
      prevCursor = null;
    };
    
    const createCircle = (position) => {
      if (!prevCursor) {
        prevCursor = position;
        return;
      }
      
      const points = pointsBetweenTwoPoints(prevCursor, position);
      points.forEach(point => {
        circles.push({ x: point.x, y: point.y, color: trailColor, iteration: 0 });
      });
      circles.push({ x: position.x, y: position.y, color: trailColor, iteration: 0 });
      prevCursor = position;
    };
    
    const exponentialDecay = (iteration, rate) => {
      return Math.exp(-rate * iteration);
    };
    
    const filterCircles = (circles, max) => {
      max = max || 300;
      const startIndex = circles.length - max;
      if (startIndex > 0) {
        circles = circles.slice(startIndex, circles.length);
      }
      circles = circles.filter(circle => circle.iteration < max);
      return circles;
    };
    
    const drawCircle = (circle) => {
      const { x, y, color, iteration } = circle;
      const alpha = exponentialDecay(iteration, rateOfIterationDecay);
      if (alpha < 0.05) return;
      
      context.beginPath();
      context.arc(x, y, circleRadius, 0, 2 * Math.PI);
      context.closePath();
      context.globalAlpha = alpha;
      context.fillStyle = color;
      context.fill();
    };
    
    const animate = () => {
      if (!context || !canvas) return;
      
      // Clear the canvas (coordinates are in viewport space, context is scaled)
      context.clearRect(0, 0, viewportWidth, viewportHeight);
      
      circles = filterCircles(circles, maxIterations);
      circles = circles.map(circle => {
        circle.iteration++;
        const circleCopy = JSON.parse(JSON.stringify(circle));
        drawCircle(circleCopy);
        return circle;
      });
      
      if (circles.length > 0) {
        animationFrame = window.requestAnimationFrame(animate);
      } else {
        animationFrame = null;
      }
    };
    
    const startAnimation = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(animate);
      }
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    window.addEventListener('mousedown', startTrail);
    window.addEventListener('mousemove', updateTrail);
    window.addEventListener('mouseup', stopTrail);
    window.addEventListener('touchstart', startTrail);
    window.addEventListener('touchmove', updateTrail);
    window.addEventListener('touchend', stopTrail);
    
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      window.removeEventListener('mousedown', startTrail);
      window.removeEventListener('mousemove', updateTrail);
      window.removeEventListener('mouseup', stopTrail);
      window.removeEventListener('touchstart', startTrail);
      window.removeEventListener('touchmove', updateTrail);
      window.removeEventListener('touchend', stopTrail);
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
  }, []);

  const handleBackClick = () => {
    if (currentStep === 1) {
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentStep(0);
        setIsFlipping(false);
      }, 300);
    }
  };

  const handleLoginClick = () => {
    setShowForm(true);
  };

  const handleArtiClick = () => {
    setCurrentPage('arti');
    window.scrollTo(0, 0);
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
    window.scrollTo(0, 0);
  };

  const handleFormClose = useCallback(() => {
    setShowForm(false);
    setFormStep(1);
    setIsSubmitting(false);
    setFormData({
      name: '',
      email: '',
      location: '',
      birthYear: '',
      track: '',
      teamType: '',
      teamMembers: '',
      projectName: '',
      shortDescription: '',
      detailedDescription: '',
      howBuilt: '',
      githubLink: '',
      videoLink: ''
    });
  }, []);

  const handleNameSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      setFormStep(2);
    }
  }, [formData.name]);

  const handleNameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleEmailSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.email.trim()) {
      setFormStep(3);
    }
  }, [formData.email]);

  const handleEmailChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, email: e.target.value }));
  }, []);

  const handleLocationSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.location.trim()) {
      setFormStep(4);
    }
  }, [formData.location]);

  const handleLocationChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, location: e.target.value }));
  }, []);

  const handleBirthYearSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.birthYear.trim()) {
      setFormStep(5); // Go to track selection
    }
  }, [formData.birthYear]);

  const handleBirthYearChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, birthYear: e.target.value }));
  }, []);


  const handleTrackSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.track) {
      setFormStep(6);
    }
  }, [formData.track]);

  const handleTrackChange = useCallback((track) => {
    setFormData(prev => ({ ...prev, track }));
  }, []);

  const handleTeamTypeSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.teamType) {
      if (formData.teamType === 'Team') {
        setFormStep(7); // Go to team members step
      } else {
        setFormStep(8); // Skip to project name step for solo
      }
    }
  }, [formData.teamType]);

  const handleTeamTypeChange = useCallback((teamType) => {
    setFormData(prev => ({ ...prev, teamType }));
  }, []);

  const handleTeamMembersSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.teamMembers.trim()) {
      setFormStep(8); // Go to project name step
    }
  }, [formData.teamMembers]);

  const handleTeamMembersChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, teamMembers: e.target.value }));
  }, []);

  const handleProjectNameSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.projectName.trim()) {
      setFormStep(9);
    }
  }, [formData.projectName]);

  const handleProjectNameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, projectName: e.target.value }));
  }, []);

  const handleShortDescriptionSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.shortDescription.trim()) {
      setFormStep(10);
    }
  }, [formData.shortDescription]);

  const handleShortDescriptionChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, shortDescription: e.target.value }));
  }, []);

  const handleDetailedDescriptionSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.detailedDescription.trim()) {
      setFormStep(11);
    }
  }, [formData.detailedDescription]);

  const handleDetailedDescriptionChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, detailedDescription: e.target.value }));
  }, []);

  const handleHowBuiltSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.howBuilt.trim()) {
      setFormStep(12);
    }
  }, [formData.howBuilt]);

  const handleHowBuiltChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, howBuilt: e.target.value }));
  }, []);

  const handleGithubLinkSubmit = useCallback((e) => {
    e.preventDefault();
    if (formData.githubLink.trim()) {
      setFormStep(13);
    }
  }, [formData.githubLink]);

  const handleGithubLinkChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, githubLink: e.target.value }));
  }, []);

  const handleVideoLinkSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (formData.videoLink.trim() && !isSubmitting) {
      // Form completed - submit to Cloudflare Worker
      setIsSubmitting(true);
      try {
        const response = await fetch('https://artificer-tally-proxy-2024-dev.bunetic-proxy.workers.dev', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();
        
        if (result.success) {
          setFormStep(14); // Show success step
        } else {
          console.error('Form submission failed:', result.error);
          alert('Form submission failed. Please try again.');
        }
      } catch (error) {
        console.error('Network error:', error);
        alert('Network error. Please check your connection and try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [formData, isSubmitting]);

  const handleVideoLinkChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, videoLink: e.target.value }));
  }, []);

  // ARTI Page Component
  const ArtiPage = () => {
    const [copied, setCopied] = useState(false);
    
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText('5RDjKNvXqs9Y5gkrThtCfpicZYYHs9MK7HhdXdUeaBLV');
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 3000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };
    
    return (
    <div className="arti-page">
      <div className="page-header">
        <div></div>
        <div className="artificer-text"><img src="/arti.svg" alt="ARTI" className="arti-icon" /></div>
        <div className="back-button" onClick={handleBackToHome}>[Back]</div>
      </div>
      
      <div className="arti-content">
        <div className="arti-blog">
          <div className="blog-header">
            <img src="/arti.svg" alt="ARTI" className="blog-icon" />
            <h1 className="blog-title">$ARTI Token</h1>
            <div className="blog-meta">
              <span className="blog-date">October 15, 2025</span>
              <span className="blog-author">Artificer</span>
            </div>
            <div className="contract-address">
              <span>CA: 5RDjKNvXqs9Y5gkrThtCfpicZYYHs9MK7HhdXdUeaBLV</span>
              <button 
                className={`copy-button ${copied ? 'copied' : ''}`}
                onClick={handleCopy}
                title="Copy address"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          
          <div className="blog-body">
            <div className="blog-section">
              <h2>What is $ARTI?</h2>
              <p>
                $ARTI is the native token of the Artificer ecosystem, designed to reward innovation, 
                creativity, and technical excellence. As an Artificer, you earn $ARTI through 
                building innovative projects and contributing 
                to the community.
              </p>
            </div>

            <div className="blog-section">
              <h2>Token Utility</h2>
              <p>
                ARTI holders can:
              </p>
              <ul>
                <li>Decide to support/invest on a project from the Artificer treasury</li>
                <li>Get exclusive access to the Artificer community chat and calls on Discord</li>
                <li>Get Limited Merch based on the amount of $ARTI you hold and luck!</li>
                <li>Stake and Lock ARTI in LP, to double your votes and earn $$ (probable in future)</li>
              </ul>
            </div>

            <div className="blog-section">
              <h2>How to Earn $ARTI</h2>
              <p>
                Start your journey as an Artificer and begin earning $ARTI today:
              </p>
              <ol>
                <li>Join our Discord community</li>
                <li>Build projects and contribute to the ecosystem</li>
                <li>Contribute to open-source initiatives</li>
                <li>Help other Artificers and share knowledge</li>
                <li>Help improve the Artificer community</li>
              </ol>
              <p>
                From the trading fees of $ARTI, exactly half of the received fees are allocated for the Artificer Treasury.
              </p>
            </div>
          </div>
          
          <div className="disclaimer-section">
            <p className="disclaimer-text">
              PLEASE NOTE
            </p>
            <p className="disclaimer-text">
              <span className="disclaimer-highlight">ARTIFICER</span> coins are intended to function as an expression of support for, and engagement with, the ideals and beliefs embodied by the symbol "<span className="disclaimer-highlight">$ARTI</span>" and the associated artwork, and are not intended to be, or to be the subject of, an investment opportunity, investment contract, or security of any type. By acquiring <span className="disclaimer-highlight">ARTIFICER</span> COIN you expressly acknowledge and agree that you acquire no rights to ownership, control, or management of any entity. <span className="disclaimer-highlight">$ARTI</span> is not available to persons or entities in restricted jurisdictions. Users acknowledge that digital assets involve inherent risks and potential for complete loss. Price data displayed on the site is from DEXScreener and may be delayed or inaccurate. Always verify current market price through multiple sources before trading. Nothing on this site constitutes financial, legal, or investment advice.
            </p>
          </div>
        </div>
      </div>
    </div>
    );
  };


  if (currentPage === 'arti') {
    return <ArtiPage />;
  }

  if (showForm) {
    return (
      <FormPage
        formStep={formStep}
        formData={formData}
        isSubmitting={isSubmitting}
        onFormClose={handleFormClose}
        onNameSubmit={handleNameSubmit}
        onNameChange={handleNameChange}
        onEmailSubmit={handleEmailSubmit}
        onEmailChange={handleEmailChange}
        onLocationSubmit={handleLocationSubmit}
        onLocationChange={handleLocationChange}
        onBirthYearSubmit={handleBirthYearSubmit}
        onBirthYearChange={handleBirthYearChange}
        onTrackSubmit={handleTrackSubmit}
        onTrackChange={handleTrackChange}
        onTeamTypeSubmit={handleTeamTypeSubmit}
        onTeamTypeChange={handleTeamTypeChange}
        onTeamMembersSubmit={handleTeamMembersSubmit}
        onTeamMembersChange={handleTeamMembersChange}
        onProjectNameSubmit={handleProjectNameSubmit}
        onProjectNameChange={handleProjectNameChange}
        onShortDescriptionSubmit={handleShortDescriptionSubmit}
        onShortDescriptionChange={handleShortDescriptionChange}
        onDetailedDescriptionSubmit={handleDetailedDescriptionSubmit}
        onDetailedDescriptionChange={handleDetailedDescriptionChange}
        onHowBuiltSubmit={handleHowBuiltSubmit}
        onHowBuiltChange={handleHowBuiltChange}
        onGithubLinkSubmit={handleGithubLinkSubmit}
        onGithubLinkChange={handleGithubLinkChange}
        onVideoLinkSubmit={handleVideoLinkSubmit}
        onVideoLinkChange={handleVideoLinkChange}
      />
    );
  }

  return (
    <div className={`App ${isScrolled ? 'scrolled' : ''}`}>
      <div className="artificer-text"><img src="/arti.svg" alt="ARTI" className="arti-icon" /></div>
      <div className="top-right-buttons">
        <div className="top-link arti-container arti-large" onClick={handleArtiClick}>
          [$ARTI]
        </div>
        <div className="top-link" onClick={handleLoginClick} style={{ display: 'none' }}>{loginText}</div>
        <a href="https://x.com/artificerdao" target="_blank" rel="noopener noreferrer" className="top-link">[X]</a>
        <a href="https://discord.gg/PH2wpMyhBF" target="_blank" rel="noopener noreferrer" className="top-link">[Discord]</a>
      </div>
      
      <div className={`flip-container ${isFlipping ? 'flipping' : ''}`}>
        {currentStep === 0 ? (
          <div className="flip-card">
      <div className="text-container">
        <div className="artificer-description">
          <div className="artificer-title">ARTIFICER</div>
          <div className="artificer-subtitle">
            <span className="word-normal"> for</span>
            <span className="word-big word-bold"> the</span>
            <span className="word-pixel"> era</span>
            <span className="word-normal"> of</span>
            <span className="word-big word-bold"> tech</span>
            <span className="word-normal">,</span>
            <span className="word-pixel word-animate"> creativity</span>
            <span className="word-normal">,</span>
            <span className="word-big word-bold word-animate"> autonomy</span>
            <span className="word-normal">,</span>
            <span className="word-pixel"> onchain</span>
            <span className="word-big word-bold word-animate"> economy</span>
          </div>
        </div>
        <div className="description-links">
          <a href="https://datahatchery.com" target="_blank" rel="noopener noreferrer" className="description-link">Blog</a>
          <a href="https://bunetic.com" target="_blank" rel="noopener noreferrer" className="description-link">Bunetic</a>
        </div>
            </div>
          </div>
        ) : (
          <div className="flip-card">
                   <div className="step-content">
                     <h2 className="step-title">Do you want to be an Artificer?</h2>
                     <div className="step-actions">
                       <button className="step-button primary" onClick={() => window.open('https://discord.gg/PH2wpMyhBF', '_blank')}>Yes</button>
                       <button className="step-button secondary" onClick={handleBackClick}>No</button>
                     </div>
                   </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
